import { Button } from '@factorialco/deprecated-design-system'
import {
  BulkDestroyTasksDocument,
  ProjectTasksDataCollectionDocument,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface DeleteModalProps {
  ids: string[]
  onCloseModal: () => void
  projectName: string
  refetchQueries?: DocumentNode[]
}

export const DeleteModal = ({
  ids,
  onCloseModal,
  projectName,
  refetchQueries,
}: DeleteModalProps) => {
  const [bulkDestroyTasks, { loading }] = useMutation(BulkDestroyTasksDocument, {
    extractorFn: (data) => data.projectManagementMutations.bulkDestroyProjectmanagementProjectTask,
  })

  const trackEvent = () => {
    track(trackingEvents.projectManagement.tasks.bulkDeleteProjectTask, {
      number: ids.length,
    })
  }

  const modalTitle = projectName
    ? i18n.tp('project_management.tasks.bulk_actions.delete.title', {
        project_name: projectName,
        count: ids.length,
      })
    : i18n.t('tasks.modals.delete.title')

  const onSubmitModal = async () => {
    trackEvent()
    const result = await bulkDestroyTasks({
      variables: { ids },
      awaitRefetchQueries: true,
      refetchQueries: refetchQueries ?? [ProjectTasksDataCollectionDocument],
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.deleted_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
        time: 2000,
      })
    }
    onCloseModal()
  }

  return (
    <Modal
      illustration={'delete'}
      title={modalTitle}
      description={i18n.tp('project_management.tasks.bulk_actions.delete.description', {
        count: ids.length,
      })}
      onClose={onCloseModal}
      onSubmit={onSubmitModal}
      footer={
        <>
          <Button type="secondary" onClick={onCloseModal}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading} submit>
            {i18n.t('common.delete')}
          </Button>
        </>
      }
    />
  )
}
